// Brewing-stand container (MB.4 — the slot UI + fuel/ingredient inventory the
// Pass-15 brew engine was missing). PURE (ENG-1).
//
// Five slots: three bottles (0..2), an ingredient (3), and blaze-powder fuel (4).
// A bottle is a `potion` item whose `components.potion` is the brew-graph key; the
// pure brew step (game/brewing.brewBatch) transforms those keys. Consuming a blaze
// powder banks 20 brews; each completed batch spends one ingredient + one brew of
// fuel. The host stores this as a block entity and ticks it in phase 6.
import { type ItemStack, type Slots, emptyStack, isEmpty, makeStack } from '../inventory';
import type { Registries } from '../../registry/load';
import { brewBatch, canBrew, BREW_TICKS } from '../brewing';

export const BREW_BOTTLES = 3;      // bottle slots are indices 0..2
export const BREW_INGREDIENT = 3;   // the ingredient slot
export const BREW_FUEL = 4;         // the blaze-powder slot
export const BREW_SIZE = 5;
/** One blaze powder fuels this many brewing operations (Java). */
export const BLAZE_POWDER_BREWS = 20;
export { BREW_TICKS };

export interface BrewingData {
  slots: (ItemStack | null)[]; // 0..2 bottles, 3 ingredient, 4 fuel
  /** Brews remaining from consumed blaze powder. */
  fuel: number;
  /** Progress 0..BREW_TICKS of the current batch. */
  ticks: number;
}

export function newBrewing(): BrewingData {
  return { slots: [null, null, null, null, null], fuel: 0, ticks: 0 };
}

export function brewingContents(d: BrewingData): ItemStack[] {
  return d.slots.filter((s): s is ItemStack => s != null && !isEmpty(s));
}

export function cloneBrewing(d: BrewingData): BrewingData {
  return { slots: d.slots.map((s) => (s && !isEmpty(s) ? { ...s } : null)), fuel: d.fuel, ticks: d.ticks };
}

/** Which items a given brewing slot accepts (empty always allowed). */
export function brewingAccepts(reg: Registries, index: number, stack: ItemStack): boolean {
  if (isEmpty(stack)) return true;
  const name = reg.items.nameOf[stack.item] ?? '';
  if (index < BREW_BOTTLES) return name === 'potion' || name === 'glass_bottle';
  if (index === BREW_FUEL) return name === 'blaze_powder';
  return index === BREW_INGREDIENT; // ingredient slot takes anything (the graph decides)
}

/** A `Slots` view over the five brewing slots for the window + transactions. */
export function brewingSlots(d: BrewingData, reg: Registries, maxStackOf: (item: number) => number): Slots {
  return {
    size: BREW_SIZE,
    get: (i) => d.slots[i] ?? emptyStack(),
    set: (i, s) => { d.slots[i] = isEmpty(s) ? null : s; },
    maxStackOf,
    accepts: (i, s) => brewingAccepts(reg, i, s),
  };
}

/** The brew-graph key a bottle slot holds (a filled potion), or null. */
function bottleKey(s: ItemStack | null): string | null {
  return s && !isEmpty(s) ? s.components?.potion ?? null : null;
}

/**
 * Advance one tick. Refuels from a blaze powder when a valid brew is pending and
 * fuel has run out; runs the BREW_TICKS timer; on completion transforms the bottle
 * keys (brewBatch), spends one ingredient and one brew of fuel. Returns true if a
 * batch completed this tick.
 */
export function tickBrewing(d: BrewingData, reg: Registries): boolean {
  const keys = d.slots.slice(0, BREW_BOTTLES).map(bottleKey);
  const ing = d.slots[BREW_INGREDIENT];
  const ingName = ing && !isEmpty(ing) ? reg.items.nameOf[ing.item] ?? null : null;
  const pending = ingName != null && canBrew(keys, ingName);

  if (pending && d.fuel <= 0) {
    const fuelStack = d.slots[BREW_FUEL];
    if (fuelStack && !isEmpty(fuelStack)) {
      d.fuel = BLAZE_POWDER_BREWS;
      fuelStack.count -= 1;
      if (fuelStack.count <= 0) d.slots[BREW_FUEL] = null;
    }
  }

  if (!pending || d.fuel <= 0) { d.ticks = 0; return false; }
  if (++d.ticks < BREW_TICKS) return false;

  const brewed = brewBatch(keys, ingName!);
  const potionId = reg.items.idOf.get('potion');
  for (let i = 0; i < BREW_BOTTLES; i++) {
    const k = brewed[i];
    if (k && k !== keys[i] && potionId !== undefined) d.slots[i] = makeStack(potionId, 1, { potion: k });
  }
  if (ing) { ing.count -= 1; if (ing.count <= 0) d.slots[BREW_INGREDIENT] = null; }
  d.fuel -= 1;
  d.ticks = 0;
  return true;
}
